/**
 * SearchResultRow View Component
 *
 * Renders a single player search result row showing the player name,
 * primary position, and team. Tapping selects the player as the
 * transfer target.
 * @module features/player-detail/components/TransferReportsSearchStep/SearchResultRow
 */

import { ChevronRight } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for SearchResultRowView */
export interface SearchResultRowViewProps {
  /** Player display name */
  readonly name: string;
  /** Position abbreviation (e.g. "CM", "ST") */
  readonly position: string | null;
  /** Team name */
  readonly teamName: string | null;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback fired when row is pressed */
  readonly onPress: () => void;
}

/**
 * Presentational row for a single search result player
 * @param root0 - Component props
 * @param root0.name - Player display name
 * @param root0.position - Position abbreviation
 * @param root0.teamName - Team name
 * @param root0.onPress - Press handler
 */
const SearchResultRowView = ({
  name,
  position,
  teamName,
  onPress,
}: SearchResultRowViewProps) => (
  <Pressable
    onPress={onPress}
    className="border-border-outline-subtle border-b px-4 py-3"
    testID="search-result-row"
  >
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack className="flex-1 gap-micro">
        <Stack direction="horizontal" className="items-center gap-2">
          <Text
            variant="title-sm"
            className="shrink text-content-primary"
            numberOfLines={1}
          >
            {name}
          </Text>
          {position ? (
            <Box className="shrink-0 rounded-sm bg-status-warning-surface px-1 py-micro">
              <Text
                variant="caption"
                className="font-medium text-status-warning"
              >
                {position}
              </Text>
            </Box>
          ) : null}
        </Stack>
        {teamName ? (
          <Text
            variant="body"
            className="text-content-secondary"
            numberOfLines={1}
          >
            {teamName}
          </Text>
        ) : null}
      </Stack>
      <Icon as={ChevronRight} size="sm" className="text-content-muted" />
    </Stack>
  </Pressable>
);

SearchResultRowView.displayName = "SearchResultRowView";

export default memo(SearchResultRowView);
